import React, { FormEvent } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useNameGuardController } from './useNameGuardController';

interface NameGuardProps {
  children: React.ReactNode;
}

/**
 * Blocks the app with a non-dismissable dialog until the user has a name.
 *
 * Wrap this outside TermsGuard so the terms dialog stacks on top and is accepted first.
 */
export const NameGuard: React.FC<NameGuardProps> = ({ children }) => {
  const { loading, nameRequired, name, saving, saveError, handleNameChange, handleSaveName } = useNameGuardController();

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleSaveName();
  };

  return (
    <>
      {nameRequired && (
        <Dialog open maxWidth="xs" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>What should we call you?</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Looks like we don&apos;t have your name yet. Add it so your team and coach know who they&apos;re
                cheering on.
              </DialogContentText>
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {saveError}
                </Alert>
              )}
              <TextField
                autoFocus
                fullWidth
                label="Your name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
            </DialogContent>
            <DialogActions>
              <Button type="submit" variant="contained" disabled={saving || !name.trim()}>
                {saving ? 'Saving...' : 'Save and Continue'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      {children}
    </>
  );
};
